import { useMemo, useState } from 'react';
import Button from '@/components/base/Button';
import { bankCode, countryByCode, searchBanks, searchCountries } from '@/utils/sendMoney';

interface BankPickerStepProps {
  countryCode: string;
  onCountry: (code: string) => void;
  bankName: string | null;
  onBank: (name: string) => void;
  onContinue: () => void;
  canContinue: boolean;
}

/**
 * "Please select the recipient's bank" — the country lives in a dropdown at the
 * top and the banks below sit in a two-column grid with their sort codes.
 */
export default function BankPickerStep({
  countryCode,
  onCountry,
  bankName,
  onBank,
  onContinue,
  canContinue,
}: BankPickerStepProps) {
  const [query, setQuery] = useState('');
  const [countryOpen, setCountryOpen] = useState(false);
  const [countryQuery, setCountryQuery] = useState('');

  const countries = useMemo(() => searchCountries(countryQuery), [countryQuery]);
  const country = countryByCode(countryCode) ?? searchCountries('')[0];
  const banks = useMemo(() => searchBanks(country, query), [country, query]);

  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-heading text-lg font-semibold text-foreground-950">
          Please select the recipient&rsquo;s bank
        </h3>
        <p className="mt-0.5 text-xs text-foreground-500">
          Select the country where the account is held and we&rsquo;ll pull up a list of national
          banks
        </p>
      </div>

      {/* Country dropdown */}
      <div>
        <span className="text-xs font-label font-medium text-foreground-500">Country</span>
        <button
          type="button"
          onClick={() => setCountryOpen((open) => !open)}
          className="mt-1 flex h-10 w-full cursor-pointer items-center justify-between border-b border-background-300 text-left"
        >
          <span className="flex items-center gap-2 text-sm text-foreground-900">
            <span className="text-base leading-none">{country.flag}</span>
            {country.name}
          </span>
          <i
            className={`ri-arrow-down-s-line text-lg text-foreground-500 transition-transform ${
              countryOpen ? 'rotate-180' : ''
            }`}
          />
        </button>

        {countryOpen ? (
          <div className="mt-2 rounded-md border border-background-200 bg-background-50 p-3">
            <input
              value={countryQuery}
              onChange={(event) => setCountryQuery(event.target.value)}
              placeholder="Search countries"
              className="h-9 w-full rounded-full border border-background-300 bg-background-50 px-4 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
            <ul className="mt-2 grid max-h-[200px] grid-cols-1 gap-x-6 overflow-y-auto pr-1 sm:grid-cols-2">
              {countries.map((item) => {
                const active = item.code === country.code;
                return (
                  <li key={item.code}>
                    <button
                      type="button"
                      onClick={() => {
                        onCountry(item.code);
                        setCountryOpen(false);
                        setCountryQuery('');
                        setQuery('');
                      }}
                      className="flex w-full cursor-pointer items-center gap-2 border-b border-background-100 py-2 text-left"
                    >
                      <span className="text-base leading-none">{item.flag}</span>
                      <span className="min-w-0 flex-1 truncate text-sm text-foreground-800">
                        {item.name}
                      </span>
                      {active ? <i className="ri-check-line text-primary-600" /> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>

      {/* Bank search */}
      <div className="relative">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search for a bank"
          className="h-11 w-full rounded-full border border-background-300 bg-background-50 pl-4 pr-11 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
        />
        <span className="pointer-events-none absolute right-4 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-base text-foreground-500">
          <i className="ri-search-line" />
        </span>
      </div>

      {/* Bank grid */}
      <ul className="grid max-h-[300px] grid-cols-1 gap-x-8 overflow-y-auto pr-1 sm:grid-cols-2">
        {banks.map((bank) => {
          const active = bankName === bank;
          return (
            <li key={bank}>
              <button
                type="button"
                onClick={() => onBank(bank)}
                className="flex w-full cursor-pointer items-center justify-between gap-3 border-b border-background-100 py-3 text-left transition-colors hover:bg-background-100/60"
              >
                <span className="min-w-0">
                  <span className="block text-sm uppercase leading-snug text-foreground-900">
                    {bank}
                  </span>
                  <span className="block text-xs text-foreground-400">{bankCode(bank)}</span>
                </span>
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                    active
                      ? 'border-primary-500 bg-primary-500 text-background-50'
                      : 'border-background-400'
                  }`}
                >
                  {active ? <i className="ri-check-line text-xs" /> : null}
                </span>
              </button>
            </li>
          );
        })}
        {banks.length === 0 ? (
          <li className="py-4 text-center text-xs text-foreground-500">
            No bank matches &ldquo;{query}&rdquo;.
          </li>
        ) : null}
      </ul>

      <Button size="sm" onClick={onContinue} disabled={!canContinue}>
        Continue
      </Button>
    </div>
  );
}